<template>
  <div>
    <Breadcrumbs :items="breadcrumbs" page_title="participantinfo" />

    <v-container fluid class="live-participant-page px-4 px-md-6 py-6">
      <v-card flat class="participant-hero rounded-xl mb-5">
        <div class="participant-hero__content">
          <div>
            <div class="participant-hero__eyebrow">{{ $t("livefeature_live_session_participants") }}</div>
            <h1 class="participant-hero__title mb-2">{{ sessionTitle }}</h1>
            <div class="participant-hero__host">{{ $t("livefeature_host_label") }}: {{ sessionHost }}</div>
            <div class="participant-hero__meta mt-3">
              <span>{{ sessionStatus }}</span>
              <span>{{ sessionSchedule }}</span>
              <span>{{ sessionDuration }}</span>
            </div>
          </div>

          <div class="participant-hero__count">
            <div class="participant-hero__count-value">{{ participantCount }}</div>
            <div class="participant-hero__count-label">{{ $t("livefeature_participants_label") }}</div>
          </div>
        </div>
      </v-card>

      <v-card flat class="participant-shell rounded-xl">
        <div class="participant-shell__toolbar">
          <v-text-field
            v-model="search"
            outlined
            dense
            clearable
            hide-details
            prepend-inner-icon="mdi-magnify"
            :placeholder="$t('livefeature_search_participants')"
            class="participant-shell__search"
          />
        </div>

        <v-alert
          v-if="loadError"
          type="error"
          dense
          outlined
          class="mx-4 mt-2 mb-0"
        >
          {{ loadError }}
        </v-alert>

        <v-alert
          v-if="apiPendingNotice"
          type="info"
          dense
          text
          class="mx-4 mt-2 mb-0"
        >
          {{ apiPendingNotice }}
        </v-alert>

        <v-data-table
          :loading="loading"
          :headers="headers"
          :items="participants"
          :search="search"
          :items-per-page="10"
          hide-default-footer
          class="participant-table"
        >
          <template v-slot:no-data>
            <div class="participant-empty pa-8 text-center">
              <div class="participant-empty__title mb-2">{{ $t("livefeature_no_participant_data") }}</div>
              <div class="participant-empty__copy">
                {{ $t("livefeature_participant_table_help") }}
              </div>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-container>
  </div>
</template>

<script>
export default {
  name: "LiveParticipant",

  data() {
    return {
      search: "",
      loading: true,
      participants: [],
      loadError: "",
    };
  },

  computed: {
    sessionId() {
      return this.$route.params.id;
    },

    sessionTitle() {
      return this.$route.query.title || `${this.$t("livefeature_live_session_prefix")} #${this.sessionId}`;
    },

    sessionHost() {
      return this.$route.query.host || this.$t("livefeature_host_pending");
    },

    sessionStatus() {
      return this.$route.query.status || this.$t("schedule");
    },

    sessionSchedule() {
      const dateLabel = this.$route.query.dateLabel || this.$t("live");
      const dateNumber = this.$route.query.dateNumber || "--";
      const time = this.$route.query.time || "--:--";

      return `${dateLabel} ${dateNumber} • ${time}`;
    },

    sessionDuration() {
      return this.$route.query.duration || this.$t("livefeature_session_time_pending");
    },

    participantCount() {
      return this.participants.length;
    },

    apiPendingNotice() {
      return this.participants.length
        ? this.$t("livefeature_showing_participants")
        : this.$t("livefeature_no_participant_enrolled");
    },

    breadcrumbs() {
      return [
        {
          sidebar_tilte: "home",
        },
        {
          text: "live",
          href: "/home/live",
        },
        {
          text: this.sessionTitle,
        },
        {
          text: "participantinfo",
        },
      ];
    },

    headers() {
      return [
        {
          text: "#",
          value: "no",
          sortable: false,
          class: "tableColor",
          width: "80px",
        },
        {
          text: this.$t("userid"),
          value: "empID",
          sortable: false,
          class: "tableColor",
        },
        {
          text: this.$t("fullnamesm"),
          value: "fullName",
          sortable: false,
          class: "tableColor",
        },
        {
          text: this.$t("group"),
          value: "departmentName",
          sortable: false,
          class: "tableColor",
        },
        {
          text: this.$t("email"),
          value: "email",
          sortable: false,
          class: "tableColor",
        },
        {
          text: this.$t("livefeature_invited_date"),
          value: "invitedDate",
          sortable: false,
          class: "tableColor",
        },
        {
          text: this.$t("livefeature_joined_date"),
          value: "signUpDate",
          sortable: false,
          class: "tableColor",
        },
        {
          text: this.$t("livefeature_status"),
          value: "status",
          sortable: false,
          class: "tableColor",
        },
      ];
    },
  },

  async created() {
    await this.fetchParticipants();
  },

  methods: {
    async fetchParticipants() {
      this.loading = true;
      this.loadError = "";

      try {
        const companyID = localStorage.getItem("companyID") || "";
        const [participantResponse, userResponse] = await Promise.all([
          this.$axios.get(
            `${this.web_url}LiveSession/GetAllParticipantsLiveSession/${this.sessionId}`,
            {
              headers: {
                accept: "*/*",
              },
            }
          ),
          this.$axios.get(
            `${this.web_urlV6}EmployeeEXT/GetEmployeeExtAllV4?id=${companyID}`,
            {
              headers: {
                accept: "*/*",
              },
            }
          ),
        ]);

        const enrollments = Array.isArray(participantResponse.data?.data)
          ? participantResponse.data.data
          : Array.isArray(participantResponse.data)
          ? participantResponse.data
          : [];

        const users = Array.isArray(userResponse.data?.data)
          ? userResponse.data.data
          : [];

        this.participants = enrollments.map((participant, index) => {
          const matchedUser = users.find((user) => {
            const candidateValues = [user.userID, user.employeeID, user.id]
              .map((value) => String(value || "").trim())
              .filter(Boolean);

            return candidateValues.includes(String(participant.userID || participant.UserID || "").trim());
          });

          return {
            no: index + 1,
            empID:
              matchedUser?.employeeID ||
              matchedUser?.userID ||
              participant.userID ||
              participant.UserID ||
              "-",
            fullName: matchedUser?.fullName || matchedUser?.name || "Unknown participant",
            departmentName:
              matchedUser?.department ||
              matchedUser?.departmentName ||
              matchedUser?.group ||
              "-",
            email: matchedUser?.email || "-",
            invitedDate: this.formatParticipantDate(participant.invitedDate || participant.InvitedDate),
            signUpDate: this.formatParticipantDate(participant.joinedDate || participant.JoinedDate),
            status: this.buildParticipantStatus(participant),
          };
        });
      } finally {
        this.loading = false;
      }
    },

    formatParticipantDate(value) {
      if (!value) {
        return "-";
      }

      const parsedDate = new Date(value);

      if (Number.isNaN(parsedDate.getTime())) {
        return "-";
      }

      return parsedDate.toLocaleString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },

    buildParticipantStatus(participant) {
      if (participant.joinFlag === true || participant.JoinFlag === true) {
        return this.$t("livefeature_status_joined");
      }

      if (participant.activeFlag === true || participant.ActiveFlag === true) {
        return this.$t("livefeature_status_invited");
      }

      return this.$t("livefeature_status_pending");
    },
  },
};
</script>

<style scoped>
.live-participant-page {
  background: linear-gradient(180deg, #f8fafc 0%, #eef3f8 100%);
  min-height: 100%;
}

.participant-hero {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
  padding: 28px;
}

.participant-hero__content {
  align-items: flex-start;
  display: flex;
  gap: 24px;
  justify-content: space-between;
}

.participant-hero__eyebrow {
  color: #16a34a;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.participant-hero__title {
  color: #0f172a;
  font-size: 1.6rem;
  font-weight: 700;
}

.participant-hero__host {
  color: #334155;
  font-size: 1rem;
}

.participant-hero__meta {
  color: #64748b;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 0.95rem;
}

.participant-hero__count {
  background: rgba(166, 204, 57, 0.12);
  border-radius: 18px;
  min-width: 150px;
  padding: 18px 20px;
  text-align: center;
}

.participant-hero__count-value {
  color: #0f172a;
  font-size: 2rem;
  font-weight: 800;
  line-height: 1;
}

.participant-hero__count-label {
  color: #475569;
  font-size: 0.92rem;
  font-weight: 600;
  margin-top: 8px;
}

.participant-shell {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.participant-shell__toolbar {
  padding: 20px 20px 8px;
}

.participant-shell__search {
  max-width: 340px;
}

.participant-empty__title {
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 700;
}

.participant-empty__copy {
  color: #64748b;
  font-size: 0.95rem;
}

::v-deep .participant-table thead th {
  background: #f8fafc;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
}

@media (max-width: 760px) {
  .participant-hero {
    padding: 22px;
  }

  .participant-hero__content {
    flex-direction: column;
  }

  .participant-hero__count {
    width: 100%;
  }
}
</style>